import { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useLocation } from 'react-router-dom';
import { ActionGetPatientPlanList } from '../../../store/Actions/AppConfigAction';
import { ActionGetTreatmentSlotById } from '../../../store/Actions/TreatmentSlotAction';
import { ActionGetServiceSlotById } from '../../../store/Actions/ServiceSlotAction';
import { ActionGetSolutionSlotById } from '../../../store/Actions/SolutionSlotAction';
import { generateQueryString, getLatestRecordId, isNullOrUndef } from '../../../utils/functions';
import { FileText, Activity, LogOut } from 'react-feather';
import { Medication } from '@mui/icons-material';
import { matchPermissions } from '../../../utils/Permission';
import { PERMISSIONS } from '../../../constants/permissions';
import { transformDoctorRoundFormFromAPI } from '../utils/dataTransformers';

/**
 * Custom hook to manage Indoor Casepaper dashboard logic.
 * Handles data fetching, status updates, and derived data calculations.
 */
export const useIndoorCasepaperLogic = () => {
    const { id } = useParams();
    const { search } = useLocation();
    const searchParams = useMemo(() => new URLSearchParams(search), [search]);

    const patientPlanId = searchParams.get('patientPlanId');
    const treatmentSlotId = searchParams.get('treatmentSlotId') || searchParams.get('slotId');
    const solutionSlotId = searchParams.get('solutionSlotId') || searchParams.get('solutionslotId');
    const serviceSlotId = searchParams.get('serviceSlotId');
    const programSlotId = searchParams.get('programSlotId');
    const roomName = searchParams.get('roomName');
    const roomType = searchParams.get('roomType');

    const dispatch = useDispatch();
    const { userDetails } = useSelector(state => state?.auth || {});
    const matchPermission = useMemo(() => matchPermissions(userDetails), [userDetails]);

    const { CcPaperDetail } = useSelector(state => state?.patient || {});
    const { patientPlanList } = useSelector(state => state?.appConfig || {});

    const [filledStatus, setFilledStatus] = useState({
        assessmentPlan: false,
        treatmentPlan: false,
        medication: false,
        yogaConsultation: false,
        dischargeForm: false
    });
    const [isStatusLoading, setIsStatusLoading] = useState(true);

    const isPatientPlanId = !!patientPlanId && !isNullOrUndef(patientPlanId);

    // Fetch basic patient plan list
    useEffect(() => {
        if (isPatientPlanId) {
            dispatch(ActionGetPatientPlanList({ Query: `/${patientPlanId}` }));
        }
    }, [patientPlanId, dispatch, isPatientPlanId]);

    // Determine correct action based on slot ID
    const slotAction = useMemo(() => {
        if (patientPlanList?.treatmentSlotId) return ActionGetTreatmentSlotById;
        if (patientPlanList?.serviceSlotId) return ActionGetServiceSlotById;
        if (patientPlanList?.solutionSlotId) return ActionGetSolutionSlotById;
        return null;
    }, [patientPlanList]);

    const activeSlotId = useMemo(() =>
        patientPlanList?.treatmentSlotId || patientPlanList?.solutionSlotId || patientPlanList?.serviceSlotId || treatmentSlotId || solutionSlotId || serviceSlotId
        , [patientPlanList, treatmentSlotId, solutionSlotId, serviceSlotId]);

    // Simplified status calculation for local data
    useEffect(() => {
        if (patientPlanList) {
            setFilledStatus(prev => {
                const newState = {
                    ...prev,
                    treatmentPlan: patientPlanList?.serviceDetails?.[Object?.keys(patientPlanList?.serviceDetails || {})?.[0]]?.[0]?.medicineValue,
                    dischargeForm: Object.keys(patientPlanList?.dischargeForm || {}).length > 0,
                    medication: Boolean(patientPlanList?.medicine?.[Object?.keys(patientPlanList?.medicine || {})?.[0]]?.[0]?.medicineName),
                };
                // Shallow compare to avoid unnecessary state updates
                if (JSON.stringify(prev) === JSON.stringify(newState)) return prev;
                return newState;
            });
        }
    }, [patientPlanList]);

    // Fetch detailed slot data for filled status indicators (assessment & yoga)
    useEffect(() => {
        if (activeSlotId && slotAction) {
            setIsStatusLoading(true);
            dispatch(slotAction({ Query: `/${activeSlotId}` }, (callback) => {
                if (callback?.statusCode === 200) {
                    const slotData = callback?.data?.[0];
                    setFilledStatus(prev => ({
                        ...prev,
                        assessmentPlan: !!slotData?.assessmentPlan?.chiefComplaint,
                        yogaConsultation: (slotData?.yogaConsultation?.[Object?.keys(slotData?.yogaConsultation || {})?.[0]]?.[0]?.exercise?.length > 0) || false,
                    }));
                }
                setIsStatusLoading(false);
            }));
        } else {
            setIsStatusLoading(false);
        }
    }, [activeSlotId, dispatch, slotAction]);

    const latestConsultationId = useMemo(() =>
        getLatestRecordId(CcPaperDetail?.consultation || [])
        , [CcPaperDetail?.consultation]);

    // Stabilize the query string by using specific primitive dependencies
    const queryString = useMemo(() =>
        generateQueryString(
            ['programSlotId', patientPlanList?.programSlotId || programSlotId],
            ['treatmentSlotId', patientPlanList?.treatmentSlotId || treatmentSlotId],
            ['slotId', patientPlanList?.treatmentSlotId || treatmentSlotId],
            ['solutionSlotId', patientPlanList?.solutionSlotId || solutionSlotId],
            ['solutionslotId', patientPlanList?.solutionSlotId || solutionSlotId],
            ['serviceSlotId', patientPlanList?.serviceSlotId || serviceSlotId],
            ['consultationId', latestConsultationId],
            ['customerId', patientPlanList?.customerId?._id || patientPlanList?.customerId || id],
            ['orderId', patientPlanList?.orderId],
            ['roomName', roomName],
            ['roomType', roomType]
        )
        , [
            patientPlanList?.programSlotId,
            programSlotId,
            patientPlanList?.treatmentSlotId,
            treatmentSlotId,
            patientPlanList?.solutionSlotId,
            solutionSlotId,
            patientPlanList?.serviceSlotId,
            serviceSlotId,
            patientPlanList?.customerId,
            patientPlanList?.orderId,
            id,
            latestConsultationId,
            roomName,
            roomType
        ]);

    const cardData = useMemo(() => {
        // If we are still loading critical initial plan, return empty or stable shell
        if (!patientPlanList && isStatusLoading) return [];

        return [
            {
                id: 0,
                title: 'Initial Assessment Form',
                description: 'Create and manage patient case papers',
                icon: FileText,
                color: '#2e37a4',
                bgColor: 'rgba(46, 55, 164, 0.1)',
                link: `/indoor-casepaper/${id}/inpatient-assessment/${patientPlanId + queryString}`,
                buttonText: filledStatus.assessmentPlan ? 'Edit CasePaper' : 'Create CasePaper',
                isFilled: filledStatus.assessmentPlan,
                hasAction: true,
                hasPermission: matchPermission(PERMISSIONS.INDOOR_CASEPAPER.INITIAL_ASSESSMENT),
                category: 'Documentation'
            },
            {
                id: 1,
                title: 'Treatment Plan',
                description: 'Create and manage treatment plan',
                icon: FileText,
                color: '#2e37a4',
                bgColor: 'rgba(46, 55, 164, 0.1)',
                link: (patientPlanList?.serviceSlotId || serviceSlotId)
                    ? `/patient-plan-details/${patientPlanList?.orderId || 'none'}${queryString}&guestId=${id}`
                    : `/indoor-casepaper/${id}/treatment-plan/${patientPlanId + queryString}`,
                buttonText: (patientPlanList?.serviceSlotId || serviceSlotId)
                    ? 'View Details'
                    : (filledStatus.treatmentPlan ? 'View/Update Plan' : 'Create Treatment Plan'),
                isFilled: (patientPlanList?.serviceSlotId || serviceSlotId) ? false : filledStatus.treatmentPlan,
                hasAction: true,
                hasPermission: matchPermission(PERMISSIONS.INDOOR_CASEPAPER.TREATMENT_PLAN),
                category: 'Documentation'
            },
            {
                id: 2,
                title: 'Medication Management',
                description: 'View and manage patient medications',
                icon: Medication,
                color: '#8b5cf6',
                bgColor: 'rgba(139, 92, 246, 0.1)',
                link: `/indoor-casepaper/${id}/medications/${patientPlanId + queryString}`,
                buttonText: filledStatus.medication ? 'Update Medications' : 'View Medications',
                isFilled: filledStatus.medication,
                hasAction: true,
                hasPermission: matchPermission(PERMISSIONS.INDOOR_CASEPAPER.MEDICATION),
                category: 'Documentation'
            },
            {
                id: 3,
                title: 'Yoga Consultation',
                description: 'Schedule and manage yoga sessions',
                icon: Activity,
                color: '#8b5cf6',
                bgColor: 'rgba(139, 92, 246, 0.1)',
                link: `/indoor-casepaper/${id}/yoga-consultation/${patientPlanId + queryString}`,
                buttonText: filledStatus.yogaConsultation ? 'Update Consultation' : 'Start Consultation',
                isFilled: filledStatus.yogaConsultation,
                hasAction: true,
                hasPermission: matchPermission(PERMISSIONS.INDOOR_CASEPAPER.YOGA),
                category: 'Therapy'
            },
            {
                id: 4,
                title: 'Discharge Form',
                description: 'Complete patient discharge process',
                icon: LogOut,
                color: '#06b6d4',
                bgColor: 'rgba(6, 182, 212, 0.1)',
                link: `/indoor-casepaper/${id}/discharge-form/${patientPlanId + queryString}`,
                buttonText: filledStatus.dischargeForm ? 'Update Discharge' : 'Discharge Patient',
                isFilled: filledStatus.dischargeForm,
                hasAction: true,
                hasPermission: matchPermission(PERMISSIONS.INDOOR_CASEPAPER.DISCHARGE),
                category: 'Documentation'
            }
        ];
    }, [id, patientPlanId, queryString, filledStatus, isStatusLoading, patientPlanList, serviceSlotId, matchPermission]);

    const normalizedPatientPlanList = useMemo(() => {
        if (!patientPlanList) return null;
        return {
            ...patientPlanList,
            doctorRoundForm: transformDoctorRoundFormFromAPI(patientPlanList.doctorRoundForm)
        };
    }, [patientPlanList]);

    return {
        id,
        patientPlanId,
        isPatientPlanId,
        patientPlanList: normalizedPatientPlanList,
        roomName,
        roomType,
        cardData,
        filledStatus,
        isStatusLoading
    };
};
